/**
 * Announcements List Component
 *
 * Displays list of announcements with CRUD operations and drag-and-drop
 * manual ordering. The studio owner drags a row by its drag handle to
 * change where it appears (in this management list AND in the public
 * widget). Reorder is optimistic: the UI updates the moment the drag
 * ends, and a snackbar surfaces the rare case where the server rejects
 * the change (in which case the previous order is restored).
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useGetAnnouncementsQuery,
  useDeleteAnnouncementMutation,
  useReorderAnnouncementsMutation,
} from '../../api/announcementsApi';

import AnnouncementEditDialog from './AnnouncementEditDialog';
import type { Announcement } from '../../types/announcements';

interface AnnouncementsListProps {
  studioId: string;
}

/**
 * Single draggable row. Pulled out as its own component because
 * useSortable() must be called from inside SortableContext, and the row
 * is busy enough (edit/delete actions + chips + drag styling) that
 * keeping it inline would obscure the DnD wiring above.
 */
interface SortableAnnouncementItemProps {
  announcement: Announcement;
  onEdit: (announcement: Announcement) => void;
  onDelete: (announcementId: string) => void;
}

const SortableAnnouncementItem: React.FC<SortableAnnouncementItemProps> = ({
  announcement,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: announcement.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged row above its neighbors and dim slightly so the
    // user gets clear "this is what I'm moving" feedback.
    zIndex: isDragging ? 1 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        // Slightly larger left padding so the drag handle has breathing room.
        pl: 1,
        backgroundColor: 'background.paper',
      }}
      secondaryAction={
        <Box>
          <IconButton onClick={() => onEdit(announcement)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => onDelete(announcement.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      }
    >
      <IconButton
        // attributes makes this the keyboard-accessible drag handle; listeners
        // wires the pointer events. Both come from useSortable() and are
        // expected to be spread onto the handle element per @dnd-kit's API.
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...attributes}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...listeners}
        size="small"
        aria-label="Drag to reorder"
        sx={{
          mr: 1,
          cursor: 'grab',
          // Make the handle inert visually (no hover ripple background) so
          // it reads as a control, not a button to click.
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary' },
          touchAction: 'none',
        }}
      >
        <DragIndicatorIcon />
      </IconButton>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1">
              {announcement.title || 'Untitled'}
            </Typography>
            {announcement.is_active ? (
              <Chip label="Active" size="small" color="success" />
            ) : (
              <Chip label="Inactive" size="small" />
            )}
            {announcement.repeat_yearly && (
              <Chip label="Repeats Yearly" size="small" variant="outlined" />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {announcement.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {announcement.date_range_display}
            </Typography>
          </Box>
        }
      />
    </ListItem>
  );
};

const AnnouncementsList: React.FC<AnnouncementsListProps> = ({ studioId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] =
    useState<Announcement | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<
    string | null
  >(null);
  const [reorderErrorOpen, setReorderErrorOpen] = useState(false);

  const {
    data: announcements,
    isLoading,
    error,
  } = useGetAnnouncementsQuery(studioId);
  const [deleteAnnouncement] = useDeleteAnnouncementMutation();
  const [reorderAnnouncements] = useReorderAnnouncementsMutation();

  // Require a small pointer movement before a drag starts. Without this,
  // clicking the drag handle to reorder can be misinterpreted as the start
  // of a drag-with-no-movement, which feels finicky.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAdd = () => {
    setEditingAnnouncement(null);
    setDialogOpen(true);
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setDialogOpen(true);
  };

  const handleDeleteClick = (announcementId: string) => {
    setAnnouncementToDelete(announcementId);
    setConfirmDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (announcementToDelete) {
      try {
        await deleteAnnouncement({
          studioId,
          announcementId: announcementToDelete,
        }).unwrap();
      } catch (err) {
        console.error('Failed to delete announcement:', err);
      }
    }
    setConfirmDialogOpen(false);
    setAnnouncementToDelete(null);
  };

  const handleDeleteCancel = () => {
    setConfirmDialogOpen(false);
    setAnnouncementToDelete(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAnnouncement(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || !announcements || active.id === over.id) {
      return;
    }
    const oldIndex = announcements.findIndex(a => a.id === active.id);
    const newIndex = announcements.findIndex(a => a.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    const newOrder = arrayMove(announcements, oldIndex, newIndex);
    const ids = newOrder.map(a => a.id);
    try {
      await reorderAnnouncements({ studioId, ids }).unwrap();
    } catch (err) {
      console.error('Failed to reorder announcements:', err);
      setReorderErrorOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load announcements. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Announcements</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Announcement
        </Button>
      </Box>

      {announcements && announcements.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={announcements.map(a => a.id)}
            strategy={verticalListSortingStrategy}
          >
            <List>
              {announcements.map(announcement => (
                <SortableAnnouncementItem
                  key={announcement.id}
                  announcement={announcement}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      ) : (
        <Alert severity="info">
          No announcements yet. Click &quot;Add Announcement&quot; to create
          one.
        </Alert>
      )}

      <AnnouncementEditDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        studioId={studioId}
        announcement={editingAnnouncement}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Announcement?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this announcement? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Surface the rare reorder failure. Optimistic patch is auto-reverted
          by the mutation's onQueryStarted handler, so the list will visibly
          snap back when this snackbar appears. */}
      <Snackbar
        open={reorderErrorOpen}
        autoHideDuration={4000}
        onClose={() => setReorderErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setReorderErrorOpen(false)}
          severity="error"
          variant="filled"
        >
          Couldn&apos;t save the new order. Your previous order has been
          restored.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnnouncementsList;
